import { createStore } from "vuex";
import type { RootState } from "@/store/types";
import { v4 as uuidv4 } from "uuid";

import wallet, { getDefaultWalletState } from "@/store/modules/wallet";
import type { Quote, ExchangePhase, Stock, MarketMover, IndexItem } from "@/utils/models";

const resetWalletModuleState = (state: RootState): void => {
  if (state.wallet && typeof state.wallet === "object") {
    Object.assign(state.wallet, getDefaultWalletState());
  }
};

const getUserSessionKey = (user: RootState["user"]): string => {
  if (!user) {
    return "";
  }

  return `${user.id ?? ""}:${user.key ?? ""}:${user.act ?? ""}`;
};

const store = createStore<RootState>({
  modules: { wallet },
  state: {
    /* current light/dark template them */
    theme: true,
    /* indicates data is being loaded and app should display a spinner */
    appReady: true,
    /* indicates that the login-to-dashboard transition is still loading */
    loginInProgress: false,
    /* logged user data */
    user: { ec: -1, dexp: "", id: 0, key: "", act: -1, wls: [], lang: "", esdbv: 0 },
    /* logged user type */
    userType: 0,
    /* quotes data */
    quotes: [] as Quote[],
    /* quote trading data */
    quoteTr: {},
    /* quote extended data */
    quoteEx: {},
    /* quote depth data */
    depth: {},
    /* exchanges data */
    exchanges: [] as Array<Record<string, unknown>>,
    /* exchanges phases */
    exchangesPhases: [] as ExchangePhase[],
    /* stocks data */
    stocks: [] as Stock[],
    /* indexed stocks by stock code and exchange code for fast access */
    indexedStocks: [] as string[],
    /* gainers list */
    gainers: [] as MarketMover[],
    /* losers list */
    losers: [] as MarketMover[],
    /* most traded list */
    mostTraded: [] as MarketMover[],
    /* indexes list */
    indexes: [] as IndexItem[],
    /* user preferred currency */
    currency: "EUR",
    /* searched stocks data in user watchlist */
    searchedStocksUserWatchlist: [] as Stock[],
    /* searched stocks data from header search item */
    searchedStocks: [] as Stock[],
    /* searched stocks data in undex watchlist */
    searchedStocksIndexesWatchlist: [] as Stock[],
    /* selected stock code for trading */
    selectedTradingCode: "",
    /* selected stock code for trading segment */
    selectedTradingCodeSegment: "",
    /* selected stock exchange for trading */
    selectedTradingExchangeId: 0,
    /* selected stock for trading share type id */
    selectedTradingCodeShareTypeId: 0,
    /* selected stock name for trading */
    selectedTradingStockName: "",
    /* selected trading action: B(uy)/S(ell) */
    selectedTradingAction: "B",
    /* selected trading quantity from depth to trading */
    selectedTradingQuantity: 0,
    /* selected trading price from depth to trading */
    selectedTradingPrice: 0,
    /* suppress quote setup when selection changes only to open the stock info view */
    suppressSelectedTradingQuoteSync: false,
    /* system messages */
    systemMessages: [] as Array<{ ord: number; [key: string]: string | number }>,
    /* indexes as watchlists */
    indexesWatchlists: [] as Array<{ ord: number; [key: string]: unknown }>,
    /* selected exchange code, 0 for all exchanges */
    selectedExchangeCode: 0,
    /* order id user wants to update */
    orderIdToUpdate: "",
    /* demo username to remember in login form */
    rememberedUsernameDemo: "",
    /* demo password to remember in login form */
    rememberedPasswordDemo: "",
    /* live username to remember in login form */
    rememberedUsernameLive: "",
    /* live password to remember in login form */
    rememberedPasswordLive: "",
    /* last user type to login */
    lastOpenLoginUserType: "live",
    /* keep demo user credentials set */
    rememberMeSetDemo: false,
    /* keep live user credentials set */
    rememberMeSetLive: false,
    /* non-logged in language */
    nonLoggedInLanguage: "EN",
    hasItemInSearch: false,
    watchlistSortingOption: 0,
    /* the width of the windows the app is working in */
    appWindowWidth: 0,
    /* set when user selects an order from wallet to update and resend */
    orderToEdit: null,
    /* watchlist/index name clicked by user */
    clickedWatchlistName: null,
    /* known exchanges indexes names */
    knownExchangeIndexes: ["SOFIX", "BGREIT", "BGBX40", "BGTR30", "DAX", "MDAX", "SDAX", "TECDAX"],
    /* stop sse while the app is intentionally replacing a stream */
    stopSse: false,
    /* withdraw requests submitted during the current browser session */
    withdrawRequests: [] as Array<Record<string, string | number>>
  },
  getters: {
    getUser(state) {
      return state.user;
    },
    isUserLogged(state) {
      return 0 === state.user?.ec;
    },
    getNonLoggedInLanguage(state) {
      return state.nonLoggedInLanguage;
    },
    getUserWatchlists(state) {
      return !state.user || !state.user?.wls ?
        [] :
        state.user.wls;
    },
    isAppReady(state) {
      return state.appReady;
    },
    isLoginInProgress(state) {
      return state.loginInProgress;
    },
    getKnownExchangeIndexes(state) {
      return state.knownExchangeIndexes;
    },
    getClickedWatchlistName(state) {
      return state.clickedWatchlistName;
    },
    getStopSse(state) {
      return state.stopSse;
    },
    getQuotes(state) {
      return state.quotes;
    },
    getQuoteTrading(state) {
      return state.quoteTr;
    },
    getQuoteExtended(state) {
      return state.quoteEx;
    },
    getDepth(state) {
      return state.depth;
    },
    getStocks(state) {
      return state.stocks;
    },
    getSearchedStocksUserWatchlist(state) {
      return state.searchedStocksUserWatchlist;
    },
    getSearchedStocks(state) {
      return state.searchedStocks;
    },
    getSearchedStocksIndexWatchlist(state) {
      return state.searchedStocksIndexesWatchlist;
    },
    getExchanges(state) {
      return state.exchanges;
    },
    getExchangesPhases(state) {
      return state.exchangesPhases;
    },
    getWallet(_state, _getters, rootState) {
      return (rootState as any).wallet;
    },
    getGainers(state) {
      return state.gainers;
    },
    getLosers(state) {
      return state.losers;
    },
    getMostTraded(state) {
      return state.mostTraded;
    },
    getIndexes(state) {
      return state.indexes;
    },
    getCurrency(state) {
      return state.currency;
    },
    getSelectedTradingCode(state) {
      return state.selectedTradingCode;
    },
    getSelectedTradingCodeSegment(state) {
      return state.selectedTradingCodeSegment;
    },
    getSelectedTradingCodeShareTypeId(state) {
      return state.selectedTradingCodeShareTypeId;
    },
    getSelectedTradingExchangeId(state) {
      return state.selectedTradingExchangeId;
    },
    getSelectedTradingStockName(state) {
      return state.selectedTradingStockName;
    },
    getSelectedTradingAction(state) {
      return state.selectedTradingAction;
    },
    getSystemMessages(state) {
      return state.systemMessages;
    },
    getSelectedTradingQuantity(state) {
      return state.selectedTradingQuantity;
    },
    getSelectedTradingPrice(state) {
      return state.selectedTradingPrice;
    },
    getIndexedStocks(state) {
      return state.indexedStocks;
    },
    getUserType(state) {
      return state.userType;
    },
    getIndexesWatchlists(state) {
      return state.indexesWatchlists;
    },
    getSelectedExchangeCode(state) {
      return state.selectedExchangeCode;
    },
    getOrderIdToUpdate(state) {
      return state.orderIdToUpdate;
    },
    getRememberedUsernameDemo(state) {
      return state.rememberedUsernameDemo;
    },
    getRememberedUsernameLive(state) {
      return state.rememberedUsernameLive;
    },
    getRememberedPasswordDemo(state) {
      return state.rememberedPasswordDemo;
    },
    getRememberedPasswordLive(state) {
      return state.rememberedPasswordLive;
    },
    getLastOpenedUserType(state) {
      return state.lastOpenLoginUserType;
    },
    getRememberMeDemo(state) {
      return state.rememberMeSetDemo;
    },
    getRememberMeLive(state) {
      return state.rememberMeSetLive;
    },
    getHasItemInSearch(state) {
      return state.hasItemInSearch;
    },
    getWatchlistSortingOption(state) {
      return state.watchlistSortingOption;
    },
    getAppWindowWidth(state) {
      return state.appWindowWidth;
    },
    getSuppressSelectedTradingQuoteSync(state) {
      return state.suppressSelectedTradingQuoteSync;
    },
    getOrderToEdit(state) {
      return state.orderToEdit;
    },
    getWithdrawRequests(state) {
      return state.withdrawRequests;
    }
  },
  mutations: {
    resetStoreState(state) {
      resetWalletModuleState(state);
      state.user = { ec: -1, dexp: "", id: 0, key: "", act: -1, wls: [], lang: "", esdbv: 0 };
      state.quotes = [];
      state.quoteTr = {};
      state.quoteEx = {};
      state.depth = {};
      state.exchanges = [];
      state.exchangesPhases = [];
      state.stocks = [];
      state.indexedStocks = [];
      state.gainers = [];
      state.losers = [];
      state.mostTraded = [];
      state.indexes = [];
      state.searchedStocksUserWatchlist = [];
      state.searchedStocks = [];
      state.searchedStocksIndexesWatchlist = [];
      state.selectedTradingCode = "";
      state.selectedTradingCodeSegment = "";
      state.selectedTradingCodeShareTypeId = 0;
      state.selectedTradingExchangeId = 0;
      state.selectedTradingStockName = "";
      state.selectedTradingAction = "B";
      state.selectedTradingQuantity = 0;
      state.selectedTradingPrice = 0;
      state.systemMessages = [];
      state.indexesWatchlists = [];
      state.selectedExchangeCode = 0;
      state.orderIdToUpdate = "";
      state.watchlistSortingOption = 0;
      state.orderToEdit = null;
      state.withdrawRequests = [];
    },
    changeTheme(state) {
      if (state.theme) {
        state.theme = false;
      } else {
        state.theme = true;
      }
    },
    setAppStateReady(state) {
      state.appReady = true;
    },
    setAppStateNotReady(state) {
      state.appReady = false;
    },
    setLoginInProgress(state, value) {
      state.loginInProgress = Boolean(value);
    },
    setUser(state, user) {
      if (getUserSessionKey(state.user) !== getUserSessionKey(user)) {
        resetWalletModuleState(state);
      }
      state.user = user;
    },
    setNonLoggedInLanguage(state, lang) {
      state.nonLoggedInLanguage = lang;
    },
    setClickedWatchlistName(state, name) {
      state.clickedWatchlistName = name;
    },
    setStopSse(state, val) {
      state.stopSse = val;
    },
    addWithdrawRequest(state, request) {
      if (request) {
        state.withdrawRequests.push(request);
      }
    },
    setQuotes(state, quotes) {
      state.quotes = quotes;
    },
    updateWatchlists(state, wls) {
      if (wls) {
        state.user.wls = wls;
      }
    },
    setQuoteTrading(state, quoteTr) {
      if (quoteTr) {
        state.quoteTr = quoteTr;
      }
    },
    setQuoteExtended(state, quoteEx) {
      if (quoteEx) {
        state.quoteEx = quoteEx;
      }
    },
    setDepth(state, depth) {
      if (depth) {
        state.depth = depth;
      }
    },
    setExchangesPhases(state, phases) {
      if (phases) {
        state.exchangesPhases = phases;
      }
    },
    upsertExchangesPhases(state, phase: Record<string, unknown>) {
      if (!phase) {
        return;
      }
      const keys = Object.keys(phase);
      const currItem = state.exchangesPhases.filter(item => {
        return 0 === item.ex.toString().toLowerCase().localeCompare(keys[0].toString().toLowerCase());
      });
      if (!currItem || 0 === currItem.length) {
        // add
        state.exchangesPhases.push({ 'ex': keys[0], 'ph': phase[keys[0]] });
      } else {
        // update
        currItem[0]['ph'] = phase[keys[0]];
      }
    },
    updateQuote(state, quote: Quote) {
      // check value
      if (!quote || !quote.b || isNaN(quote?.b)) {
        return;
      }
      const item = state.quotes.filter(quoteItem => {
        return 0 === quoteItem.xc.toString().toLowerCase().localeCompare(quote.xc.toString().toLowerCase())
          && 0 === quoteItem.sc.toString().toLowerCase().localeCompare(quote.sc.toString().toLowerCase())
      });
      if (item && item[0]) {
        // update
        const update = item[0] as Quote;
        // update values
        update.b = quote.b;
        update.a = quote.a;
        update.dif = quote.dif;
        update.ch = quote.ch;
        update.t = new Date().getTime() - quote.t;

        item[0] = update;
      } else {
        // insert new
        state.quotes.push(quote);
      }
    },
    setStocks(state, stocks: Stock[]) {
      if (stocks) {
        state.stocks = stocks;
        // indexed stocks
        const inxStocks: string[] = [];
        stocks.forEach((stock: Stock) => {
          // mic + code must be unique from server
          inxStocks.push(stock.cMIC + stock.code);
        });
        state.indexedStocks = inxStocks;
      }
    },
    setSearchedStocksUserWatchlist(state, stocks) {
      state.searchedStocksUserWatchlist = stocks;
    },
    setSearchedStocks(state, stocks) {
      state.searchedStocks = stocks;
    },
    setSearchedStocksIndexWatchlist(state, stocks) {
      state.searchedStocksIndexesWatchlist = stocks;
    },
    addSearchedStockUserWatchlist(state, payload) {
      if (!payload.reset) {
        state.searchedStocksUserWatchlist.push(payload.stock);
      } else {
        const newItems = [];
        if (null !== payload.stock) {
          newItems.push(payload.stock);
        }
        state.searchedStocksUserWatchlist = newItems;
      }
    },
    addSearchedStockIndexWatchlist(state, payload) {
      if (!payload.reset) {
        state.searchedStocksIndexesWatchlist.push(payload.stock);
      } else {
        const newItems = [];
        if (null !== payload.stock) {
          newItems.push(payload.stock);
        }
        state.searchedStocksIndexesWatchlist = newItems;
      }
    },
    addStock(state, stock) {
      if (stock) {
        state.stocks.push(stock);
        state.indexedStocks.push(stock.cMIC + stock.code);
      }
    },
    updateStock(state, stock) {
      if (!stock) {
        return;
      }

      const stockIndex = state.indexedStocks.indexOf(stock.cMIC + stock.code);
      if (0 <= stockIndex) {
        state.stocks[stockIndex] = {
          ...state.stocks[stockIndex],
          ...stock
        };
      }
    },
    setExchanges(state, exchanges) {
      if (exchanges) {
        state.exchanges = exchanges;
      }
    },

    setGainers(state, gainers) {
      state.gainers = gainers;
    },
    updateGainers(state, gainer: MarketMover) {
      const existing = state.gainers.find((it: MarketMover) =>
        it.sc.toString().toLowerCase() === gainer.sc.toString().toLowerCase()
      );
      if (existing) {
        existing.dv = gainer.dv;
        existing.dif = gainer.dif;
        existing.ch = gainer.ch;
        existing.lp = gainer.lp;
        if (gainer.ex !== undefined) existing.ex = gainer.ex;
      }
    },
    updateGainersList(state, gainers: { list: MarketMover[] }) {
      if (!gainers?.list || gainers.list.length === 0) return;

      const newItems: MarketMover[] = gainers.list.map((item: MarketMover) => ({
        sc: item.sc,
        dv: item.dv,
        dif: item.dif,
        ch: item.ch,
        lp: item.lp,
        ex: item.ex
      }));

      state.gainers = newItems;
    },
    setLosers(state, losers) {
      if (losers) {
        state.losers = losers;
      }
    },
    updateLosers(state, loser: MarketMover) {
      const existing = state.losers.find((it: MarketMover) =>
        it.sc.toString().toLowerCase() === loser.sc.toString().toLowerCase()
      );
      if (existing) {
        existing.dv = loser.dv;
        existing.dif = loser.dif;
        existing.ch = loser.ch;
        existing.lp = loser.lp;
        if (loser.ex !== undefined) existing.ex = loser.ex;
      }
    },
    updateLosersList(state, losers: { list: MarketMover[] }) {
      if (!losers?.list || losers.list.length === 0) return;

      const newItems: MarketMover[] = losers.list.map((item: MarketMover) => ({
        sc: item.sc,
        dv: item.dv,
        dif: item.dif,
        ch: item.ch,
        lp: item.lp,
        ex: item.ex
      }));

      state.losers = newItems;
    },
    setMostTraded(state, mostTraded) {
      if (mostTraded) {
        state.mostTraded = mostTraded;
      }
    },
    updateMostTraded(state, mt: MarketMover) {
      const existing = state.mostTraded.find((it: MarketMover) =>
        it.sc.toString().toLowerCase() === mt.sc.toString().toLowerCase()
      );
      if (existing) {
        existing.dv = mt.dv;
        existing.dif = mt.dif;
        existing.ch = mt.ch;
        existing.lp = mt.lp;
        if (mt.ex !== undefined) existing.ex = mt.ex;
      }
    },
    updateMostTradedList(state, mostTraded: { list: MarketMover[] }) {
      if (!mostTraded?.list || mostTraded.list.length === 0) return;

      const newItems: MarketMover[] = mostTraded.list.map((item: MarketMover) => ({
        sc: item.sc,
        dv: item.dv,
        dif: item.dif,
        ch: item.ch,
        lp: item.lp,
        ex: item.ex
      }));

      state.mostTraded = newItems;
    },
    setIndexes(state, indexes: IndexItem[]) {
      if (!indexes || indexes.length === 0) return;

      // Normalize to the fields we render/use
      const newItems: IndexItem[] = indexes.map((item: IndexItem) => ({
        sc: item.sc,
        val: item.val,
        dif: item.dif,
        ch: item.ch,
        t: item.t
      }));

      state.indexes = newItems;
    },
    updateIndex(state, index: IndexItem) {
      const existing = state.indexes.find((it: IndexItem) =>
        it.sc.toString().toLowerCase() === index.sc.toString().toLowerCase()
      );

      if (existing) {
        existing.t = index.t;
        existing.val = index.val;
        existing.dif = index.dif;
        existing.ch = index.ch;
      } else {
        state.indexes.push(index);
      }
    },
    updateUserLanguage(state, lang) {
      if (null !== state.user) {
        state.user.lang = lang;
      }
      state.nonLoggedInLanguage = lang;
    },
    setCurrency(state, curr) {
      state.currency = curr;
    },
    setSelectedTradingCode(state, code) {
      state.selectedTradingCode = code;
    },
    setSelectedTradingCodeSegment(state, code) {
      state.selectedTradingCodeSegment = code;
    },
    setSelectedTradingCodeShareTypeId(state, code) {
      state.selectedTradingCodeShareTypeId = code;
    },
    setSelectedTradingExchangeId(state, exchangeId) {
      state.selectedTradingExchangeId = exchangeId;
    },
    setSelectedTradingStockName(state, name) {
      state.selectedTradingStockName = name;
    },
    setSelectedTradingAction(state, action) {
      state.selectedTradingAction = action;
    },
    addSystemMessage(state, message) {
      state.systemMessages.push(message);
    },
    resetSystemMessages(state) {
      state.systemMessages = [];
    },
    setSelectedTradingQuantity(state, qty) {
      state.selectedTradingQuantity = qty;
    },
    setSelectedTradingPrice(state, prc) {
      state.selectedTradingPrice = prc;
    },
    setUserType(state, type) {
      /* 0: demo, 1: real */
      state.userType = type;
    },
    setIndexesWatchlists(state, arr) {
      state.indexesWatchlists = arr;
    },
    addIndexesWatchlists(state, payload) {
      const nextItem = {
        ...payload.arr,
        ord: payload.index
      };
      const nextKey = nextItem.wln?.toString().toUpperCase();
      const sameItems = state.indexesWatchlists.filter(item => {
        return item.wln?.toString().toUpperCase() !== nextKey;
      });
      sameItems.push(nextItem);
      state.indexesWatchlists = sameItems.sort((a, b) => a.ord > b.ord ? 1 : -1);
    },
    setSelectedExchangeCode(state, code) {
      state.selectedExchangeCode = code;
    },
    setOrderIdToUpdate(state, oid) {
      state.orderIdToUpdate = oid;
    },
    setRememberedUsernameDemo(state, username) {
      state.rememberedUsernameDemo = username;
    },
    setRememberedUsernameLive(state, username) {
      state.rememberedUsernameLive = username;
    },
    setRememberedPasswordDemo(state, password) {
      state.rememberedPasswordDemo = password;
    },
    setRememberedPasswordLive(state, password) {
      state.rememberedPasswordLive = password;
    },
    setLastOpenedUserType(state, type) {
      state.lastOpenLoginUserType = type;
    },
    setRememberMeDemo(state, doRemember) {
      state.rememberMeSetDemo = doRemember;
    },
    setRememberMeLive(state, doRemember) {
      state.rememberMeSetLive = doRemember;
    },
    setHasItemInSearch(state, val) {
      state.hasItemInSearch = val;
    },
    setWatchlistSortingOption(state, val) {
      state.watchlistSortingOption = val
    },
    setAppWindowWidth(state, val) {
      state.appWindowWidth = val;
    },
    setSuppressSelectedTradingQuoteSync(state, val) {
      state.suppressSelectedTradingQuoteSync = val;
    },
    setOrderToEdit(state, val) {
      state.orderToEdit = val;
    }
  },
  actions: {
    async loginUser({ commit }, payload): Promise<number> {
      const loginFailedError = -1;
      const incorrectCharsetError = 9301;
      // use post
      let usrAndPass = payload.username + ':' + payload.password;
      try {
        usrAndPass = btoa(usrAndPass);
        // swap case
        let swappedString = '';
        let i = 0;
        while (i < usrAndPass.length) {
          let n = usrAndPass.charAt(i);
          n = n == n.toUpperCase()
            ? n.toLowerCase()
            : n.toUpperCase();
          swappedString += n;
          i++;
        }
        usrAndPass = btoa(swappedString);
      } catch (e) {
        // this is only for btoa as it requires latin1
        // using a regexp will be tricky, so we just capture the exception and test if its
        // because user entered utf characters
        console.error("Incorrect charset");
        return incorrectCharsetError;
      }

      try {
        const res = await payload.api.getApiClient(payload.demo ? 0 : 1).post("", {}, {
          params: {
            a: payload.demo
              ? "demo_login_ex"
              : "login_client_ex"
          },
          headers: {
            "Authorization": "Basic " + usrAndPass
          }
        });
        if (res) {
          // check answer
          if (0 === res.data.ec) {
            commit("setUser", res.data);
            commit("setNonLoggedInLanguage", res.data.lang || "EN");
            commit("setUserType", payload.demo ? 0 : 1);
            // set webapp generated device_id and fb_token to comply with logout policy
            const deviceId = "webapp-" + uuidv4();
            // set it no matter what, this will be used at a later point when "keep me logged in" is implemented
            await payload.api.getApiClient(payload.demo ? 0 : 1).get("", {
              params: {
                a: payload.demo
                  ? "demo_login"
                  : "login_client",
                id: res.data.id,
                key: res.data.key,
                dev_id: encodeURIComponent(deviceId)
              }
            });
          }
          return res.data.ec;
        }
        return loginFailedError;
      } catch (e) {
        console.error(e);
        return loginFailedError;
      }
    },
    async getQuotes({ state, commit }, payload): Promise<void> {
      try {
        const res = await payload.api.getApiClient(state.userType).get("", {
          params: {
            a: "get_quotes",
            id: state.user.id,
            key: state.user.key,
            sc: 0 === payload.quotes.length ? null : payload.quotes.join()
          }
        });
        if (res) {
          // check answer
          if (0 === res.data.ec) {
            // update quotes
            commit("setQuotes", res.data.list);
          }
        }
      } catch (e) {
        console.error(e);
      }
    },
    async setSseQuotes({ state }, payload): Promise<void> {
      try {
        await payload.api.getApiClient(state.userType).get("", {
          params: {
            a: "set_sse",
            id: state.user.id,
            key: state.user.key,
            "quote-set": 0 === payload.quotes.length ? null : payload.quotes.join(),
            "quote-sts": 0 === payload.quotes.length ? "off" : "on"
          }
        });
      } catch (e) {
        console.error(e);
      }
    }
  }
});

export default store;
